// File: frontend/src/components/admin/AdminLegal.tsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaGavel, FaSave, FaEye, FaCode, FaExclamationTriangle } from "react-icons/fa";
import {
    getAdminLegalDocuments,
    updateAdminLegalDocument,
    AdminLegalDocument,
} from "../../services/adminLegalService";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import "./AdminLegal.css";

interface AdminLegalProps {
    isAdmin: boolean;
}

// ── Display labels ────────────────────────────────────────────────────────────

const DOC_LABELS: Record<string, string> = {
    TERMS:   "Kullanım Koşulları",
    PRIVACY: "Gizlilik Politikası",
    COOKIE:  "Çerez Politikası",
    RULES:   "Hamur Abi Kuralları",
};

const LANG_LABELS: Record<string, string> = {
    tr: "Türkçe",
    en: "English",
};

/**
 * Token reference shown next to the editor.
 *
 * Listing them here is not decoration: an admin cannot guess the exact key
 * spelling, and a mistyped token silently renders as an em dash in a published
 * legal text. Keeping the list visible next to the textarea is the cheapest way
 * to prevent that.
 */
const TOKEN_HINTS: string[] = [
    "{{setting:site.name}}",
    "{{setting:legal.company.name}}",
    "{{setting:legal.site.domain}}",
    "{{setting:legal.kvkk.email}}",
    "{{setting:legal.kvkk.kep}}",
    "{{setting:legal.jurisdiction}}",
    "{{setting:legal.min_age}}",
    "{{setting:legal.content_retention_years}}",
    "{{setting:legal.log_retention_years}}",
    "{{setting:contact.email.general}}",
    "{{setting:contact.email.complaint}}",
    "{{setting:entry.max_length}}",
    "{{setting:entry.daily_limit}}",
    "{{setting:entry.edit_window_minutes}}",
    "{{setting:messaging.max_length}}",
    "{{setting:moderation.report_cooldown_days}}",
    "{{setting:media.entry_image_max_mb}}",
    "{{setting:media.avatar_max_mb}}",
    "{{setting:user.username.max_length}}",
    "{{const:ACCOUNT_DELETION_GRACE_DAYS}}",
];

/**
 * Editor for the three legal documents in both languages.
 *
 * The version field is the important one: users.terms_version stores the exact
 * version a user accepted, so bumping it after a material change is what makes
 * "who is still on the old agreement" an answerable question. The UI warns when
 * the body changed but the version did not.
 */
export const AdminLegal: React.FC<AdminLegalProps> = ({ isAdmin }) => {
    const { t } = useTranslation();

    const [documents, setDocuments] = useState<AdminLegalDocument[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPreview, setShowPreview] = useState(false);

    // Draft state for the currently selected document
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [version, setVersion] = useState("");
    const [effectiveDate, setEffectiveDate] = useState("");
    const [published, setPublished] = useState(true);

    const selected = useMemo(
        () => documents.find((d) => d.id === selectedId) ?? null,
        [documents, selectedId]
    );

    // ── Load ─────────────────────────────────────────────────────

    const loadDocuments = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getAdminLegalDocuments();
            setDocuments(data);
            // Select the first document only on the initial load, so a save does
            // not bounce the admin back to the top of the list
            setSelectedId((current) => (current !== null ? current : data[0]?.id ?? null));
        } catch (e) {
            setError(
                e instanceof Error
                    ? e.message
                    : t("admin_legal_load_error", "Yasal metinler yüklenemedi.")
            );
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (isAdmin) loadDocuments();
    }, [isAdmin, loadDocuments]);

    // Reset the draft whenever the selection changes
    useEffect(() => {
        if (!selected) return;
        setTitle(selected.title);
        setBody(selected.body);
        setVersion(selected.version);
        setEffectiveDate(selected.effectiveDate ?? "");
        setPublished(selected.published);
        setShowPreview(false);
    }, [selected]);

    // ── Save ─────────────────────────────────────────────────────

    const bodyChanged = selected !== null && body !== selected.body;
    const versionUnchanged = selected !== null && version === selected.version;

    const handleSave = async () => {
        if (!selected) return;

        if (!title.trim() || !body.trim() || !version.trim()) {
            setError(t("admin_legal_required", "Başlık, metin ve sürüm boş bırakılamaz."));
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const updated = await updateAdminLegalDocument(selected.id, {
                title: title.trim(),
                body,
                version: version.trim(),
                effectiveDate: effectiveDate || null,
                published,
            });
            // Replace in place so the list keeps its order and the selection holds
            setDocuments((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
            setSuccess(t("admin_legal_saved", "Metin kaydedildi."));
        } catch (e) {
            setError(
                e instanceof Error
                    ? e.message
                    : t("admin_legal_save_error", "Metin kaydedilemedi.")
            );
        } finally {
            setSaving(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────

    if (!isAdmin) {
        return (
            <div className="admin-legal">
                <div className="legal-admin-empty">
                    {t("admin_legal_admin_only", "Bu bölüm yalnızca yöneticilere açıktır.")}
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="admin-legal">
                <div className="legal-admin-empty">{t("loading", "Yükleniyor...")}</div>
            </div>
        );
    }

    return (
        <div className="admin-legal">
            {/* Banner props follow the shared AdminBanner contract: success is
                dismissible, error is not. */}
            <AdminSuccessBanner message={success || null} onDismiss={() => setSuccess("")} />
            <AdminErrorBanner message={error || null} />

            <div className="legal-admin-layout">
                {/* ── Left: document list ── */}
                <aside className="legal-admin-list">
                    <div className="legal-admin-list-header">
                        <FaGavel className="legal-admin-list-icon" />
                        <span>{t("admin_legal_documents", "Yasal Metinler")}</span>
                    </div>

                    {documents.map((doc) => (
                        <button
                            key={doc.id}
                            type="button"
                            className={`legal-admin-list-item ${doc.id === selectedId ? "active" : ""}`}
                            onClick={() => setSelectedId(doc.id)}
                        >
                            <span className="legal-admin-item-title">
                                {DOC_LABELS[doc.docType] ?? doc.docType}
                            </span>
                            <span className="legal-admin-item-meta">
                                {LANG_LABELS[doc.language] ?? doc.language} · v{doc.version}
                                {!doc.published && (
                                    <span className="legal-admin-unpublished">
                                        {t("admin_legal_unpublished", "yayında değil")}
                                    </span>
                                )}
                            </span>
                        </button>
                    ))}
                </aside>

                {/* ── Right: editor ── */}
                <section className="legal-admin-editor">
                    {!selected ? (
                        <div className="legal-admin-empty">
                            {t("admin_legal_select", "Düzenlemek için soldan bir metin seçin.")}
                        </div>
                    ) : (
                        <>
                            <div className="legal-admin-field">
                                <label className="legal-admin-label">
                                    {t("admin_legal_title", "Başlık")}
                                </label>
                                <input
                                    type="text"
                                    className="legal-admin-input"
                                    value={title}
                                    maxLength={200}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>

                            <div className="legal-admin-field-row">
                                <div className="legal-admin-field">
                                    <label className="legal-admin-label">
                                        {t("admin_legal_version", "Sürüm")}
                                    </label>
                                    <input
                                        type="text"
                                        className="legal-admin-input"
                                        value={version}
                                        maxLength={20}
                                        onChange={(e) => setVersion(e.target.value)}
                                    />
                                </div>

                                <div className="legal-admin-field">
                                    <label className="legal-admin-label">
                                        {t("admin_legal_effective_date", "Yürürlük Tarihi")}
                                    </label>
                                    <input
                                        type="date"
                                        className="legal-admin-input"
                                        value={effectiveDate}
                                        onChange={(e) => setEffectiveDate(e.target.value)}
                                    />
                                </div>

                                <div className="legal-admin-field legal-admin-field--checkbox">
                                    <label className="legal-admin-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={published}
                                            onChange={(e) => setPublished(e.target.checked)}
                                        />
                                        <span>{t("admin_legal_published", "Yayında")}</span>
                                    </label>
                                </div>
                            </div>

                            {/* Bumping the version after a material edit is what lets the
                                platform tell who accepted which text, and what resets the
                                cookie choice. Worth a visible nudge. */}
                            {bodyChanged && versionUnchanged && (
                                <div className="legal-admin-warning">
                                    <FaExclamationTriangle />
                                    <span>
                                        {t(
                                            "admin_legal_version_warning",
                                            "Metni değiştirdiniz ama sürüm numarasını yükseltmediniz. Esaslı bir değişiklikse sürümü artırın."
                                        )}
                                    </span>
                                </div>
                            )}

                            <div className="legal-admin-field">
                                <div className="legal-admin-body-header">
                                    <label className="legal-admin-label">
                                        {t("admin_legal_body", "Metin")}
                                    </label>
                                    <button
                                        type="button"
                                        className="legal-admin-toggle"
                                        onClick={() => setShowPreview((v) => !v)}
                                    >
                                        {showPreview ? <FaCode /> : <FaEye />}
                                        <span>
                                            {showPreview
                                                ? t("admin_legal_edit", "Düzenle")
                                                : t("admin_legal_preview", "Önizleme")}
                                        </span>
                                    </button>
                                </div>

                                {showPreview ? (
                                    // Preview uses the saved rendered body from the server, so
                                    // it shows exactly what a visitor would see. Unsaved edits
                                    // are intentionally not previewed — rendering them here
                                    // would require duplicating the token resolver client-side.
                                    <div
                                        className="legal-admin-preview legal-body"
                                        dangerouslySetInnerHTML={{ __html: selected.renderedBody }}
                                    />
                                ) : (
                                    <textarea
                                        className="legal-admin-textarea"
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        spellCheck={false}
                                    />
                                )}

                                {showPreview && bodyChanged && (
                                    <p className="legal-admin-hint">
                                        {t(
                                            "admin_legal_preview_stale",
                                            "Önizleme son kaydedilen hâli gösterir. Değişikliklerinizi görmek için kaydedin."
                                        )}
                                    </p>
                                )}
                            </div>

                            {/* Token reference */}
                            <div className="legal-admin-tokens">
                                <span className="legal-admin-tokens-title">
                                    {t("admin_legal_tokens", "Kullanılabilir değişkenler")}
                                </span>
                                <div className="legal-admin-tokens-list">
                                    {TOKEN_HINTS.map((token) => (
                                        <code key={token} className="legal-admin-token">
                                            {token}
                                        </code>
                                    ))}
                                </div>
                                <p className="legal-admin-hint">
                                    {t(
                                        "admin_legal_tokens_hint",
                                        "Bu değişkenler metin yayınlanırken canlı ayar değerleriyle değiştirilir. Karşılığı boş olan bir değişken kısa çizgi olarak görünür."
                                    )}
                                </p>

                                {/* Conditional syntax. Several settings treat 0 as
                                    "no limit", where printing the number would say
                                    the opposite of what the code does. */}
                                <span className="legal-admin-tokens-title">
                                    {t("admin_legal_conditions", "Koşullu blok")}
                                </span>
                                <code className="legal-admin-condition">
                                    {"{{if:entry.daily_limit=0}}Sınır yoktur.{{else}}Hakkınız {{setting:entry.daily_limit}} adettir.{{endif}}"}
                                </code>
                                <p className="legal-admin-hint">
                                    {t(
                                        "admin_legal_conditions_hint",
                                        "Ayar 0 iken sınırsız anlamına gelen alanlarda kullanın; doğru cümle yayın anında kendiliğinden seçilir. {{else}} isteğe bağlıdır, iç içe kullanılamaz. \"!=\" da desteklenir."
                                    )}
                                </p>
                            </div>

                            <div className="legal-admin-actions">
                                {selected.updatedAt && (
                                    <span className="legal-admin-updated">
                                        {t("admin_legal_last_update", "Son güncelleme")}:{" "}
                                        {new Date(selected.updatedAt).toLocaleString("tr-TR")}
                                        {selected.updatedByUsername && ` · ${selected.updatedByUsername}`}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="legal-admin-save"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    <FaSave />
                                    <span>{saving ? t("saving", "Kaydediliyor...") : t("save", "Kaydet")}</span>
                                </button>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};